// Client-side telemetry — Application Insights browser SDK.
//
// Everything we knew about performance before this existed was SERVER-side: how
// long an Azure Function took, not what a person waited for. That measured the
// wrong thing twice over. It missed bundle download, render and time-to-
// interactive entirely, and it could not tell a human apart from a machine —
// the busiest "endpoint" in the app is a label-printer poll on the Prism VM at
// 76,480 calls a fortnight, which nobody waits on. `client_IP` came back as a
// single distinct value, so the server side could not separate callers either.
//
// Ships to the SAME App Insights resource the Functions host already writes to
// (abaris-backend-functions-app-insights), so browser and backend telemetry sit
// in one place and can be joined on timestamp.
//
// LOADED AS A SEPARATE CHUNK, deliberately. Statically importing the SDK put
// +77 kB gzipped on the main bundle — an 18% increase on a bundle that is
// already the biggest thing warehouse tablets wait for. Measuring a problem
// should not make it worse. The dynamic import below keeps main.js as it was
// and fetches the SDK in parallel with the first render; calls made before it
// lands are queued, not dropped, so the first page view still arrives.
// Navigation timing is read from the browser's performance API, which is
// populated regardless of when the SDK loads, so page-load numbers are intact.
//
// Config lives in REACT_APP_APPINSIGHTS_CONNECTION_STRING. Absent — local dev,
// or a build that forgot it — every function here is a safe no-op rather than a
// crash. Nothing in the app should ever fail because telemetry is unavailable.

import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { API_URL } from './api';

const CONNECTION_STRING = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING || '';

// Telemetry goes to OUR domain, which forwards it (blueprints/telemetry.py).
//
// Posting straight to *.in.applicationinsights.azure.com does not work on real
// staff machines: that hostname is on EasyPrivacy and every default blocklist,
// and Edge's built-in Tracking Prevention blocks it with no extension at all.
// Confirmed on a live browser 2026-08-30 — net::ERR_BLOCKED_BY_CLIENT, 0 B, the
// request never left. Blockers match on hostname, so a first-party URL gets
// through where the Azure one is dropped silently.
//
// The route is called ui-diagnostics rather than anything honest because
// "track", "telemetry", "analytics", "collect" and "beacon" are all matched as
// PATH patterns by the same blocklists.
const ENDPOINT_URL = `${API_URL}/ui-diagnostics`;

type Action = (ai: ApplicationInsights) => void;

let appInsights: ApplicationInsights | null = null;
let loading: Promise<void> | null = null;
const pending: Action[] = [];

/** Run now if the SDK is up, queue if it is still loading, drop if disabled. */
function withAI(action: Action): void {
  if (appInsights) {
    try {
      action(appInsights);
    } catch {
      /* telemetry must never throw into the app */
    }
  } else if (loading) {
    pending.push(action);
  }
}

/**
 * Collapse ids out of a path so page views GROUP.
 *
 * Without this, /containers/1234 and /containers/1235 are two different pages
 * and the "which screens do people actually use" question stays unanswerable —
 * every screen with a row id fragments into hundreds of one-visit entries.
 * Numeric and uuid-ish segments become :id.
 */
export function normalisePath(pathname: string): string {
  return pathname
    .split('/')
    .map((seg) => {
      if (/^\d+$/.test(seg)) return ':id';
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) return ':id';
      return seg;
    })
    .join('/');
}

export function initTelemetry(): void {
  if (loading || !CONNECTION_STRING) return;

  loading = import('@microsoft/applicationinsights-web')
    .then(({ ApplicationInsights: AI }) => {
      const ai = new AI({
        config: {
          connectionString: CONNECTION_STRING,

          // userOverrideEndpointUrl, NOT endpointUrl. Setting endpointUrl next
          // to a connectionString does nothing: the SDK parses the connection
          // string and overwrites it, and the only thing it will not overwrite
          // is this key. From ai.3.4.3.js:17789 —
          //
          //   _config.endpointUrl = _config[userOverrideEndpointUrl]
          //       ? _config[userOverrideEndpointUrl]
          //       : ingest + DEFAULT_BREEZE_PATH;
          //
          // It fails silently and looks like it works: telemetry keeps flowing
          // from browsers that do not block Azure (Safari did), and vanishes
          // from the ones that do (Edge), which reads as "the relay is not
          // deployed yet" rather than "the config key is wrong".
          userOverrideEndpointUrl: ENDPOINT_URL,

          // Route changes are tracked manually in <RouteTelemetry/>. The
          // built-in auto-tracking hooks the History API, which reports raw
          // URLs (ids and all) and double-fires on some react-router v6
          // transitions.
          enableAutoRouteTracking: false,

          // Time every fetch/XHR from the BROWSER's side. This is the number
          // that matters and that server telemetry cannot see: a 366ms endpoint
          // called 66 times to paint one board is not a 366ms experience.
          disableFetchTracking: false,
          disableAjaxTracking: false,
          enableRequestHeaderTracking: false,
          enableResponseHeaderTracking: false,

          // MUST stay false. True makes the SDK add Request-Id / traceparent
          // headers to cross-origin calls, and the API is a different origin
          // from this app. The backend's CORS allow-list is exactly
          // "Content-Type, Authorization, X-Requested-With" (session_auth.py
          // get_cors_headers) — trace headers are not on it, so every preflight
          // would fail and the whole app would stop talking to the API. Wiring
          // browser-to-backend correlation means widening that list FIRST.
          enableCorsCorrelation: false,

          // Errors users hit that we currently never hear about.
          disableExceptionTracking: false,
          enableUnhandledPromiseRejectionTracking: true,

          autoTrackPageVisitTime: true,
          disableCookiesUsage: false,
        },
      });
      ai.loadAppInsights();
      appInsights = ai;

      // Drain anything that happened while the chunk was in flight — typically
      // the first page view and the restored user context.
      const queued = pending.splice(0, pending.length);
      queued.forEach((action) => {
        try {
          action(ai);
        } catch {
          /* ignore */
        }
      });
    })
    .catch((err) => {
      // Chunk blocked (ad blockers commonly eat App Insights) or offline.
      // eslint-disable-next-line no-console
      console.warn('App Insights unavailable; continuing without telemetry', err);
      pending.length = 0;
    });
}

export function trackPageView(name: string, uri: string): void {
  withAI((ai) => ai.trackPageView({ name, uri }));
}

/**
 * Attach a staff number to everything this browser sends from here on.
 *
 * This is what turns "203 endpoints and some traffic" into "who is waiting, on
 * what screen, for how long" — and it is the only way to get a real count of
 * how many humans use the system, which we have never had.
 *
 * staff_no, deliberately, not name or email: it identifies the person for
 * support purposes without putting a directory of staff email addresses into
 * telemetry.
 */
export function setTelemetryUser(staffNo: string): void {
  if (!staffNo) return;
  withAI((ai) => ai.setAuthenticatedUserContext(String(staffNo), undefined, true));
}

export function clearTelemetryUser(): void {
  withAI((ai) => ai.clearAuthenticatedUserContext());
}

/** A named thing a user did — use for actions worth counting, not for logging. */
export function trackEvent(name: string, properties?: Record<string, unknown>): void {
  withAI((ai) => ai.trackEvent({ name }, properties));
}

export function trackException(error: Error, properties?: Record<string, unknown>): void {
  // SeverityLevel.Error === 3. Hard-coded rather than imported so this module
  // pulls in no runtime code from the SDK — importing the enum would defeat the
  // dynamic-chunk split above.
  withAI((ai) => ai.trackException({ exception: error, severityLevel: 3 }, properties));
}

/** True when telemetry is actually running — for debugging, not control flow. */
export function isTelemetryEnabled(): boolean {
  return appInsights !== null;
}
